import { useState, useEffect } from "react";
import { Send, AlertCircle, Calendar, Clock, ChevronDown, ChevronUp, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "./adminApi";
import { displayName, sortByLastName } from "@/lib/formatters";

export default function SMSTab() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [content, setContent] = useState("");
  const [smsType, setSmsType] = useState("transactional");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [expandedLogId, setExpandedLogId] = useState(null); // log_id du log ouvert (détail d'erreur)

  useEffect(() => {
    api.get("/admin/sms/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/admin/sms/logs").then((r) => {
      setLogs(r.data);
      // Auto-expand the most recent error so the admin immediately sees WHY Brevo refused,
      // without having to click through every red badge.
      const firstErr = (r.data || []).find((l) => l.status === "error" && l.error);
      if (firstErr) setExpandedLogId(firstErr.log_id);
    }).catch(() => {});
    api.get("/candidates").then((r) => setCandidates(r.data)).catch(() => {});
  }, []);

  const refreshLogs = () => api.get("/admin/sms/logs").then((r) => setLogs(r.data)).catch(() => {});

  const filtered = sortByLastName(candidates.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.city || "").toLowerCase().includes(s) || (c.phone || "").toLowerCase().includes(s);
  }));

  const consentField = smsType === "marketing" ? "sms_marketing_consent" : "sms_operational_consent";
  const consentLabel = smsType === "marketing" ? "SMS marketing" : "SMS opérationnel";
  const eligibleCount = [...selected].filter((id) => candidates.find((c) => c.user_id === id)?.[consentField]).length;
  const totalEligibleInList = filtered.filter((c) => c[consentField] && c.phone).length;
  const totalWithoutConsent = filtered.filter((c) => !c[consentField] && c.phone).length;
  const totalWithoutPhone = filtered.filter((c) => !c.phone).length;

  const toggle = (id) => { const s = new Set(selected); if (s.has(id)) s.delete(id); else s.add(id); setSelected(s); };
  const selectAll = () => setSelected(new Set(filtered.filter((c) => c[consentField] && c.phone).map((c) => c.user_id)));
  const clearSel = () => setSelected(new Set());

  const segments = (() => {
    if (!content) return 0;
    const isUni = !/^[\x20-\x7E\n€£¥èéàù]*$/.test(content);
    const limit = isUni ? 70 : 160;
    const concat = isUni ? 67 : 153;
    return content.length <= limit ? 1 : Math.floor((content.length - limit) / concat) + 2;
  })();

  const sendBulk = async () => {
    if (!content.trim() || selected.size === 0) { toast.error("Sélectionnez au moins un candidat et saisissez un message"); return; }
    setSending(true);
    try {
      const r = await api.post("/admin/sms/send-bulk", { user_ids: [...selected], content, sms_type: smsType, campaign: `bulk_${Date.now()}` });
      const d = r.data;
      toast.success(`Envoi : ${d.sent.length} OK, ${d.skipped.length} ignorés, ${d.errors.length} erreurs`);
      setContent("");
      clearSel();
      refreshLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'envoi"); }
    finally { setSending(false); }
  };

  const scheduleBulk = async () => {
    if (!content.trim() || selected.size === 0) { toast.error("Sélectionnez au moins un candidat et saisissez un message"); return; }
    if (!scheduleAt) { toast.error("Choisissez une date et heure d'envoi"); return; }
    setScheduling(true);
    try {
      const r = await api.post("/admin/sms/schedule", {
        user_ids: [...selected], content, sms_type: smsType,
        scheduled_at: scheduleAt,
        campaign: `sched_${Date.now()}`,
      });
      toast.success(`SMS programmé pour ${new Date(r.data.scheduled_at).toLocaleString("fr-FR")} — ${selected.size} destinataire(s)`);
      setContent("");
      setScheduleAt("");
      clearSel();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur programmation"); }
    finally { setScheduling(false); }
  };

  return (
    <div data-testid="sms-tab" className="space-y-6">
      {status && !status.configured && (
        <div className="bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Brevo non configuré</p>
            <p>La clé <code>BREVO_API_KEY</code> n'est pas définie. Les SMS seront simulés (status=simulated dans les logs). Renseignez la clé dans <code>backend/.env</code> pour les activer.</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Composer */}
        <div className="lg:col-span-2 bg-white border border-gray-200 p-6 space-y-4">
          <h3 className="text-sm font-bold text-[#3F3F3F]">Composer un SMS</h3>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={smsType} onValueChange={(v) => { setSmsType(v); clearSel(); }}>
              <SelectTrigger className="rounded-none" data-testid="sms-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transactional">Opérationnel (mission, rappel)</SelectItem>
                <SelectItem value="marketing">Marketing (offre, relance)</SelectItem>
              </SelectContent>
            </Select>
            <div className={`mt-2 p-2 text-[11px] border ${smsType === "marketing" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-blue-50 border-blue-200 text-blue-800"}`}>
              {smsType === "marketing" ? (
                <>📣 <strong>Mode marketing</strong> — seuls les candidats ayant accepté le <strong>SMS marketing</strong> apparaîtront comme éligibles. Les autres seront grisés et non sélectionnables (obligation RGPD).</>
              ) : (
                <>⚙️ <strong>Mode opérationnel</strong> — seuls les candidats ayant accepté le <strong>SMS opérationnel</strong> sont éligibles. Les autres seront grisés et ignorés.</>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} maxLength={480}
              placeholder="Votre message..." className="w-full border border-gray-300 p-2 text-sm" data-testid="sms-content" />
            <p className="text-[11px] text-gray-500 mt-1">{content.length}/480 — {segments} segment{segments > 1 ? "s" : ""} ({segments * (selected.size || 1)} crédits estimés)</p>
          </div>
          <Button onClick={sendBulk} disabled={sending || scheduling || selected.size === 0 || !content.trim()}
            className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none w-full" data-testid="send-sms-btn">
            <Send className="w-4 h-4 mr-2" />{sending ? "Envoi…" : `Envoyer maintenant à ${eligibleCount} candidat(s) éligible(s) (${selected.size} sélectionné(s))`}
          </Button>
          <div className="border-t border-gray-100 pt-3 space-y-2" data-testid="sms-schedule-block">
            <Label className="text-xs font-semibold flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Ou programmer l&apos;envoi</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="rounded-none text-sm h-9 flex-1 min-w-[220px]"
                data-testid="sms-schedule-at"
                min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
              />
              <Button
                onClick={scheduleBulk}
                disabled={scheduling || sending || !scheduleAt || selected.size === 0 || !content.trim()}
                variant="outline"
                className="rounded-none text-xs border-[#662483] text-[#662483] hover:bg-[#F0E9F3] h-9"
                data-testid="schedule-sms-btn"
              >
                <Clock className="w-3.5 h-3.5 mr-1" />
                {scheduling ? "Programmation…" : "Programmer"}
              </Button>
            </div>
            <p className="text-[10px] text-gray-500">Fuseau Europe/Paris. Le SMS sera envoyé automatiquement à la date et heure choisies.</p>
          </div>
        </div>

        {/* Recipients selector */}
        <div className="bg-white border border-gray-200 p-4 space-y-3">
          <h3 className="text-sm font-bold text-[#3F3F3F]">Destinataires — {consentLabel}</h3>
          <Input placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none" data-testid="sms-recipient-search" />
          <div className="grid grid-cols-3 gap-1 text-[10px] text-center">
            <div className="bg-green-50 border border-green-200 p-1.5">
              <p className="font-bold text-green-700 text-sm">{totalEligibleInList}</p>
              <p className="text-green-700">Éligibles</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 p-1.5" title={`${totalWithoutConsent} candidat(s) sans consentement ${consentLabel}`}>
              <p className="font-bold text-amber-700 text-sm">{totalWithoutConsent}</p>
              <p className="text-amber-700">Sans consent.</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-1.5">
              <p className="font-bold text-gray-700 text-sm">{totalWithoutPhone}</p>
              <p className="text-gray-700">Sans tél.</p>
            </div>
          </div>
          <div className="flex gap-2 text-xs flex-wrap">
            <button onClick={selectAll} className="text-[#0069B4] hover:underline" data-testid="sms-select-all-eligible">
              Sélectionner les {totalEligibleInList} éligibles
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => {
                const ids = filtered.filter((c) => c.in_mission && c.phone && c[consentField]).map((c) => c.user_id);
                setSelected(new Set(ids));
                toast.success(`${ids.length} salarié(s) en mission sélectionné(s)`);
              }}
              className="text-[#662483] hover:underline font-semibold"
              data-testid="sms-select-in-mission"
              title="Tous les salariés actuellement assignés à une mission (et éligibles)"
            >
              🟢 Salariés en mission ({filtered.filter((c) => c.in_mission && c.phone && c[consentField]).length})
            </button>
            {selected.size > 0 && <button onClick={clearSel} className="text-red-500 hover:underline ml-auto">Effacer ({selected.size})</button>}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {filtered.slice(0, 100).map((c) => {
              const hasPhone = !!c.phone;
              const hasConsent = !!c[consentField];
              const ok = hasPhone && hasConsent;
              const reason = !hasPhone ? "📵 Pas de tél" : !hasConsent ? `🚫 Pas de consentement ${consentLabel}` : "";
              return (
                <label key={c.user_id} className={`flex items-center gap-2 py-2 px-1 cursor-pointer ${ok ? "" : "opacity-60 cursor-not-allowed"}`} data-testid={`sms-recipient-${c.user_id}`} title={reason}>
                  <input type="checkbox" disabled={!ok} checked={selected.has(c.user_id)} onChange={() => toggle(c.user_id)} className="shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#3F3F3F] truncate">{displayName(c)}</p>
                    <p className="text-[10px] text-gray-500 truncate">{c.phone || "—"} {!ok && <span className="text-amber-600 ml-1">{reason}</span>}</p>
                  </div>
                  {ok ? (
                    <Badge className="rounded-none bg-green-100 text-green-700 text-[9px] shrink-0">OK</Badge>
                  ) : !hasPhone ? (
                    <Badge className="rounded-none bg-gray-100 text-gray-600 text-[9px] shrink-0">📵</Badge>
                  ) : (
                    <Badge className="rounded-none bg-amber-100 text-amber-700 text-[9px] shrink-0">🚫</Badge>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Logs */}
      <div className="bg-white border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#3F3F3F]">Historique d'envoi ({logs.length})</h3>
          <button onClick={refreshLogs} className="text-xs text-[#0069B4] hover:underline">Rafraîchir</button>
        </div>
        <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
          {logs.slice(0, 50).map((l) => {
            const isError = l.status === "error";
            const isExpanded = expandedLogId === l.log_id;
            const canExpand = isError && !!l.error;
            return (
              <div key={l.log_id} className="text-xs" data-testid={`sms-log-${l.log_id}`}>
                <div
                  className={`px-4 py-2 flex items-center gap-3 ${canExpand ? "cursor-pointer hover:bg-red-50" : ""}`}
                  onClick={() => canExpand && setExpandedLogId(isExpanded ? null : l.log_id)}
                  title={canExpand ? "Cliquer pour voir le détail de l'erreur" : ""}
                  data-testid={`sms-log-row-${l.log_id}`}
                >
                  <Badge className={`rounded-none ${l.status === "sent" ? "bg-green-100 text-green-700" : l.status === "simulated" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>{l.status}</Badge>
                  <span className="font-mono text-gray-500 shrink-0">{l.phone_e164 || l.phone_input}</span>
                  <span className="flex-1 truncate text-[#3F3F3F]">{l.content?.slice(0, 80)}</span>
                  <span className="text-gray-400 shrink-0">{l.created_at?.slice(0, 16).replace("T", " ")}</span>
                  {canExpand && (isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-red-600 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-red-600 shrink-0" />)}
                </div>
                {isError && (
                  <div className={`px-4 pb-2 -mt-1 ${isExpanded ? "block" : "hidden"}`} data-testid={`sms-log-error-${l.log_id}`}>
                    <div className="bg-red-50 border border-red-200 p-2 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Détail Brevo</span>
                          {l.http_status && <span className="text-[10px] font-mono bg-red-100 text-red-800 px-1">HTTP {l.http_status}</span>}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(l.error || ""); toast.success("Copié"); }}
                            className="ml-auto text-[10px] text-red-700 hover:text-red-900 flex items-center gap-1"
                            data-testid={`sms-log-copy-${l.log_id}`}
                          >
                            <Copy className="w-3 h-3" /> Copier
                          </button>
                        </div>
                        <pre className="text-[11px] text-red-900 whitespace-pre-wrap break-all font-mono leading-tight" data-testid={`sms-log-error-text-${l.log_id}`}>
                          {l.error || "(pas de détail retourné)"}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
